import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

import { getExternalToolkitLabel } from '@aline/common';
import { useSnackbar } from 'notistack';
import { Apollo } from 'src/gql/generated';
import { ExternalToolkitSlug } from 'src/gql/generated/apollo-types';

import { getExternalToolkitMutationErrorMessage } from './external-toolkit-errors';
import { EXTERNAL_TOOLKIT_CONNECTED_MESSAGE } from './ExternalToolkitConnectedPage';

const CONNECTION_POLL_MS = 2_000;

type ConnectLinkMutationOptions = Parameters<typeof Apollo.useConnectExternalToolkitMutation>[0];

/** Start a provider connect and return the authorization URL. */
export function useExternalToolkitConnectLink(mutationOptions?: ConnectLinkMutationOptions) {
  const [connect, { loading }] = Apollo.useConnectExternalToolkitMutation(mutationOptions);

  const requestConnectLink = useCallback(
    async (toolkit: ExternalToolkitSlug) => {
      const result = await connect({ variables: { toolkit } });
      const redirectUrl = result.data?.connectExternalToolkit.redirectUrl;
      if (!redirectUrl) throw new Error('Missing external toolkit redirect URL.');
      return redirectUrl;
    },
    [connect]
  );

  return { loading, requestConnectLink };
}

/**
 * Connect from a surface that stays on screen (chat). Claims a tab on the
 * click, sends it to the provider, and watches the catalogue until the
 * account is live. Polling is the proof; the callback message and a return
 * to this tab only refetch sooner.
 */
export function useExternalToolkitProviderConnect(toolkit: string) {
  const { enqueueSnackbar } = useSnackbar();
  const providerTabRef = useRef<Window | null>(null);
  const [awaitingProvider, setAwaitingProvider] = useState(false);
  const { loading: connecting, requestConnectLink } = useExternalToolkitConnectLink();
  const { data, refetch } = Apollo.useGetExternalToolkitsQuery({
    fetchPolicy: 'network-only',
    pollInterval: awaitingProvider ? CONNECTION_POLL_MS : 0,
  });
  const isConnected = getIsToolkitConnected(data?.externalToolkits, toolkit);

  useEffect(() => {
    if (!awaitingProvider) return;
    return watchProviderHandoff({
      onClosed: () => setAwaitingProvider(false),
      onMaybeConnected: () => void refetch(),
      providerTabRef,
    });
  }, [awaitingProvider, refetch]);

  const closeProviderTab = useCallback(() => {
    closeWindow(providerTabRef);
    setAwaitingProvider(false);
  }, []);

  const connect = useCallback(
    async ({ abort }: { abort?: () => boolean } = {}) => {
      if (!getIsOfferedToolkit(toolkit)) {
        enqueueSnackbar(`Aline cannot connect ${toolkit} accounts.`, { variant: 'error' });
        return;
      }

      const providerTab = window.open('', '_blank');
      if (!providerTab) {
        enqueueSnackbar(`Could not connect ${getExternalToolkitLabel(toolkit)}. Try again.`, {
          variant: 'error',
        });
        return;
      }

      try {
        setAwaitingProvider(true);
        providerTabRef.current = providerTab;
        const redirectUrl = await requestConnectLink(toolkit);
        if (abort?.() || providerTab.closed) {
          providerTabRef.current = null;
          setAwaitingProvider(false);
          return;
        }
        providerTab.location.replace(redirectUrl);
      } catch (error) {
        providerTab.close();
        setAwaitingProvider(false);
        enqueueSnackbar(
          getExternalToolkitMutationErrorMessage(
            error,
            `Could not connect ${getExternalToolkitLabel(toolkit)}. Try again.`
          ),
          { variant: 'error' }
        );
      }
    },
    [enqueueSnackbar, requestConnectLink, toolkit]
  );

  return {
    awaitingProvider,
    busy: connecting || awaitingProvider,
    closeProviderTab,
    connect,
    isConnected,
  };
}

function getIsToolkitConnected(
  toolkits: readonly { slug: string; status: string }[] | undefined,
  toolkit: string
) {
  return Boolean(
    toolkits?.some(
      entry =>
        entry.slug.toLowerCase() === toolkit.toLowerCase() &&
        entry.status === Apollo.ExternalToolkitConnectionStatus.Connected
    )
  );
}

function getIsOfferedToolkit(toolkit: string): toolkit is ExternalToolkitSlug {
  return Object.values(ExternalToolkitSlug).some(slug => slug === toolkit);
}

function watchProviderHandoff({
  onClosed,
  onMaybeConnected,
  providerTabRef,
}: {
  onClosed: () => void;
  onMaybeConnected: () => void;
  providerTabRef: MutableRefObject<Window | null>;
}) {
  const handleMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    if (event.data !== EXTERNAL_TOOLKIT_CONNECTED_MESSAGE) return;
    onMaybeConnected();
  };
  const handleVisibility = () => {
    if (document.visibilityState === 'visible') onMaybeConnected();
  };
  const timer = window.setInterval(() => {
    if (!providerTabRef.current?.closed) return;
    providerTabRef.current = null;
    onClosed();
  }, CONNECTION_POLL_MS);

  window.addEventListener('message', handleMessage);
  document.addEventListener('visibilitychange', handleVisibility);

  return () => {
    window.removeEventListener('message', handleMessage);
    document.removeEventListener('visibilitychange', handleVisibility);
    window.clearInterval(timer);
  };
}

function closeWindow(ref: MutableRefObject<Window | null>): void {
  const providerTab = ref.current;
  ref.current = null;
  if (!providerTab) return;
  try {
    if (!providerTab.closed) providerTab.close();
  } catch {
    /* Already gone, or closed by the user. */
  }
}
